import type { CampusResponder, EmergencyAlert, SafetyIncident } from '../types/safety'

/**
 * Seeded demonstration records. Response channels are deliberately mixed:
 * one campus works the desk, one relays through a control room, one has no
 * agreement at all — because that is the honest range, and the product must
 * behave correctly in the worst of the three.
 */
export const campusResponders: CampusResponder[] = [
  {
    campusId: 'campus-akoka',
    campusName: 'Akoka Main Campus',
    institutionName: 'University of Lagos',
    channel: 'console',
    unit: 'UNILAG Security Command, Akoka',
    phone: '+234 803 000 1111',
    hours: 'Desk staffed 24 hours',
    medianAckMinutes: 3,
    agreementConfirmed: '14 January 2026',
  },
  {
    campusId: 'campus-idi-araba',
    campusName: 'Idi-Araba College of Medicine',
    institutionName: 'University of Lagos',
    channel: 'relay',
    unit: 'Idi-Araba Security Control Room',
    phone: '+234 806 000 4422',
    hours: 'Desk staffed 08:00–18:00; relayed by phone outside those hours',
    medianAckMinutes: 11,
    agreementConfirmed: '2 March 2026',
  },
  {
    campusId: 'campus-ife',
    campusName: 'Ile-Ife Main Campus',
    institutionName: 'Obafemi Awolowo University',
    channel: 'none',
    unit: 'OAU Security Division',
    phone: '+234 805 000 7788',
    hours: 'Not connected to Campuzon',
    medianAckMinutes: null,
    agreementConfirmed: null,
  },
]

export function responderForCampus(campusId: string): CampusResponder | undefined {
  return campusResponders.find((item) => item.campusId === campusId)
}

export const seedAlerts: EmergencyAlert[] = [
  {
    id: 'alert-1',
    reference: 'SOS-4471',
    kind: 'security',
    state: 'closed',
    campusId: 'campus-akoka',
    raisedBy: { userId: 'CAMP-4471209', name: 'Tobi Adeleye', handle: 'tobi' },
    raisedAt: '11 August 2026, 22:41',
    disclosure: { location: true, trustedContacts: true, note: true },
    coordinates: { lat: 6.5158, lng: 3.3898, accuracyMetres: 14 },
    placeHint: 'Behind Moremi Hall, near the generator house',
    note: 'Two people following me from the back gate.',
    trustedContactsNotified: ['Mrs A. Adeleye (mother)'],
    responder: 'Sgt. E. Bassey, UNILAG Security Command',
    outcome:
      'Patrol reached the location in 6 minutes and walked the student to Moremi Hall. No injury. Back gate lighting logged as a maintenance fault.',
    timeline: [
      { at: '22:41', label: 'Alert raised by student', actor: '@tobi' },
      { at: '22:41', label: 'Delivered to UNILAG Security Command desk', actor: 'Campuzon' },
      { at: '22:43', label: 'Acknowledged', actor: 'Sgt. E. Bassey' },
      { at: '22:44', label: 'Trusted contact notified by SMS', actor: 'Campuzon' },
      { at: '22:44', label: 'Patrol dispatched', actor: 'Sgt. E. Bassey' },
      { at: '22:47', label: 'Responder in contact with student', actor: 'Sgt. E. Bassey' },
      { at: '23:02', label: 'Closed with outcome', actor: 'Sgt. E. Bassey' },
    ],
  },
  {
    id: 'alert-2',
    reference: 'SOS-4488',
    kind: 'medical',
    state: 'acknowledged',
    campusId: 'campus-akoka',
    raisedBy: { userId: 'CAMP-8820114', name: 'Ngozi Ekwueme', handle: 'ngozi' },
    raisedAt: '14 August 2026, 15:12',
    disclosure: { location: true, trustedContacts: false, note: true },
    coordinates: { lat: 6.5171, lng: 3.3962, accuracyMetres: 22 },
    placeHint: 'Faculty of Engineering, ground floor corridor',
    note: 'A classmate has collapsed. She is breathing.',
    trustedContactsNotified: [],
    responder: 'Nurse O. Balogun, Campus Health Centre',
    outcome: null,
    timeline: [
      { at: '15:12', label: 'Alert raised by student', actor: '@ngozi' },
      { at: '15:12', label: 'Delivered to UNILAG Security Command desk', actor: 'Campuzon' },
      { at: '15:13', label: 'Acknowledged', actor: 'Nurse O. Balogun' },
      { at: '15:14', label: 'Health centre notified', actor: 'Nurse O. Balogun' },
    ],
  },
]

export const seedIncidents: SafetyIncident[] = [
  {
    id: 'inc-1',
    reference: 'INC-2214',
    category: 'Unsafe environment',
    campusId: 'campus-akoka',
    location: 'Back gate footpath, no working lights',
    detail:
      'The whole stretch between the back gate and Moremi Hall is unlit after 19:00. Students walk it every evening.',
    reported: '3 August 2026',
    anonymous: false,
    status: 'Action taken',
    responderNote:
      'Three lamp posts logged with Works and Services on 5 August. Two replaced, one awaiting a part. Patrol frequency on that path raised to hourly until it is done.',
  },
  {
    id: 'inc-2',
    reference: 'INC-2260',
    category: 'Theft',
    campusId: 'campus-akoka',
    location: 'Faculty of Engineering reading room',
    detail: 'Laptop taken from a desk between 14:00 and 14:20.',
    reported: '9 August 2026',
    anonymous: false,
    status: 'Under review',
    responderNote: null,
  },
  {
    id: 'inc-3',
    reference: 'INC-2271',
    category: 'Harassment',
    campusId: 'campus-akoka',
    location: 'Withheld by reporter',
    detail:
      'Repeated unwanted contact from a person who is not a student. Filed anonymously; the reporter did not want to be contacted.',
    reported: '12 August 2026',
    anonymous: true,
    status: 'Received',
    responderNote: null,
  },
]
